import {
  Question,
  QuestionData,
  QuestionnaireData,
  Answer,
  AnswerMap,
  Page,
  PageGroup,
  Questionnaire,
} from "../context/questionnaire/types";

const DEFAULT_PRIVACY_URL = "https://canceriq.com/privacy-policy";

export function resolveLandingConfig(data?: QuestionnaireData) {
  return {
    splashTitle: data?.pp_splash_title ?? data?.splash_title,
    splashImage: data?.pp_splash_image ?? data?.splash_image,
    surveyDescription: data?.pp_survey_description ?? data?.survey_description,
    privacyPolicyUrl:
      data?.pp_privacy_policy_url ??
      data?.privacy_policy_url ??
      DEFAULT_PRIVACY_URL,
    startButtonTitle: data?.start_button_title,
  };
}

type OperatorFn = (a: any, b: any) => boolean;

const operators: Record<string, OperatorFn> = {
  "+": (a, b) => a + b,
  "<": (a, b) => a < b,
  ">": (a, b) => a > b,
  "<=": (a, b) => a <= b,
  ">=": (a, b) => a >= b,
  "==": (a, b) => a == b,
  has: (a, b) =>
    Array.isArray(a) || typeof a === "string" ? a.indexOf(b) > -1 : false,
};

function applyOperator(
  operator: string,
  lop: any,
  rop: any,
  inverse?: string | boolean
): boolean {
  const fn = operators[operator];
  if (!fn) {
    console.error("Unknown operator:", operator);
    return false;
  }
  const result = fn(lop, rop);
  return inverse === "true" || inverse === true ? !result : result;
}

function testCondition(
  cond: {
    operator: string;
    value: any;
    inverse?: string | boolean;
    "question-name": string;
    key?: string;
  },
  answer: Answer | undefined
): boolean {
  if (answer === undefined || answer === null) {
    return false;
  }

  const numericAnswer = parseFloat(answer.value);
  if (answer.value === numericAnswer) {
    return applyOperator(
      cond.operator,
      numericAnswer,
      cond.value,
      cond.inverse
    );
  }

  if (!answer.isValid) return false;

  const questionType = answer.question?.data?.type;

  if (questionType === "choice_multi") {
    const values = (answer.values || []).map((v: any) => v.value);
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  if (questionType === "columns_select_number") {
    const values = (answer.value || [])
      .filter((v: any) => v.selectVal !== undefined)
      .map((v: any) => v.selectVal.value);
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  if (questionType === "multi_checkbox") {
    const values = Object.keys(answer.value || {}).filter(
      (k) => answer.value[k]
    );
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  const value =
    cond.key !== undefined ? answer.value[cond.key] : answer.value;
  return applyOperator(cond.operator, value, cond.value, cond.inverse);
}

function getAnswerByName(
  answers: AnswerMap,
  name: string,
  _checking?: Set<string>
): Answer | undefined {
  const answer = answers[name];
  if (!answer) return undefined;
  const refQuestion = answer.question;
  if (refQuestion?.data?.conditions || refQuestion?.data?.conditionsOr) {
    const checking = _checking || new Set<string>();
    if (checking.has(name)) return answer;
    checking.add(name);
    if (!evaluateConditionsInner(refQuestion, answers, checking)) return undefined;
  }
  return answer;
}

function evaluateConditionsInner(
  question: Question,
  answers: AnswerMap,
  checking: Set<string>
): boolean {
  if (!question.data) {
    return true;
  }
  const conditions = question.data.conditions;
  const conditionsOr = question.data.conditionsOr;

  let conditionsMet = true;
  let conditionsOrMet = false;

  if (!conditions) conditionsMet = true;
  if (!conditionsOr) conditionsOrMet = true;

  if (conditions) {
    for (const cond of conditions) {
      const answer = getAnswerByName(answers, cond["question-name"], checking);
      conditionsMet = testCondition(cond, answer);
      if (!conditionsMet) break;
    }
  }

  if (conditionsOr) {
    for (const cond of conditionsOr) {
      const answer = getAnswerByName(answers, cond["question-name"], checking);
      if (testCondition(cond, answer)) {
        conditionsOrMet = true;
      }
    }
  }

  return conditionsMet && conditionsOrMet;
}

export function evaluateConditions(
  question: Question,
  answers: AnswerMap
): boolean {
  return evaluateConditionsInner(question, answers, new Set());
}

export function buildPages(
  questionnaire: Questionnaire | null,
  questions: Question[]
): Page[] {
  if (!questionnaire?.survey_modules) return [];

  const nameToGroup: Record<string, PageGroup> = {};

  for (const mod of questionnaire.survey_modules) {
    const moduleGroups = mod.data?.page_groups || [];
    for (const grp of moduleGroups) {
      if (!grp?.questions || !Array.isArray(grp.questions)) continue;
      for (const qName of grp.questions) {
        if (!qName) continue;
        if (nameToGroup[qName] == null) {
          nameToGroup[qName] = grp;
        }
      }
    }
  }

  const pages: Page[] = [];
  let i = 0;

  while (i < questions.length) {
    const question = questions[i];
    const qName = question.data?.name;
    const group = qName ? (nameToGroup[qName] ?? null) : null;
    const groupId = group?.id;

    const page: Page = {
      startIndex: i,
      endIndex: i,
      group,
      questions: [{ index: i, data: question.data }],
    };

    if (groupId) {
      let j = i + 1;
      while (j < questions.length) {
        const next = questions[j];
        const nextName = next.data?.name;
        const nextGroup = nextName ? (nameToGroup[nextName] ?? null) : null;
        if (nextGroup?.id === groupId) {
          page.questions.push({ index: j, data: next.data });
          page.endIndex = j;
          j += 1;
        } else {
          break;
        }
      }
      i = j;
    } else {
      i += 1;
    }

    pages.push(page);
  }

  return pages;
}

export function getAnswerKey(answer: Answer): string {
  const questionData = answer.question.data;
  return questionData.answer_key || questionData.name;
}

export function formatAnswer(answer: Answer): any {
  const question = answer.question.data;
  const basicAnswers = [
    "choice",
    "date_input",
    "select",
    "number",
    "multi_checkbox",
  ];

  if (basicAnswers.includes(question.type)) {
    if (question.type === "multi_checkbox") {
      const v = answer.value;
      if (v && typeof v === "object" && !Array.isArray(v)) return v;
      const fromValues = answer.values?.[0];
      if (
        fromValues &&
        typeof fromValues === "object" &&
        !Array.isArray(fromValues)
      ) {
        return fromValues;
      }
      return v;
    }
    return answer.value;
  }

  switch (question.type) {
    case "choice_multi": {
      return (answer.values || []).map((v: any) => v.value);
    }

    case "columns_select_number": {
      const names = question.answer_names || ["column1", "column2"];
      return (answer.value || [])
        .filter((v: any) => v.selectVal?.value !== undefined)
        .map((v: any) => {
          const entry: Record<string, any> = {};
          if (v.id !== undefined) entry.id = v.id;
          entry[names[0]] = v.selectVal.value;
          entry[names[1]] = v.inputText;
          return entry;
        });
    }

    case "choice_multi_dropdown": {
      return (answer.values || []).map((v: any) => {
        const entry: Record<string, any> = { value: v.value };
        if (v.selectVal) {
          const col2Name = question.dropdown_name || "column2";
          entry[col2Name] = v.selectVal.value;
        }
        return entry;
      });
    }

    case "columns_two_input": {
      const names = question.answer_names || ["column1", "column2"];
      return (answer.value || [])
        .filter((v: any) => v.inputText1 !== undefined)
        .map((v: any) => {
          const entry: Record<string, any> = {};
          if (v.id !== undefined) entry.id = v.id;
          entry[names[0]] = v.inputText1;
          entry[names[1]] = v.inputText2;
          return entry;
        });
    }

    case "phone_number": {
      const phoneValues = answer.values || [];
      const primaryPhone = phoneValues[0]?.value;
      if (primaryPhone === undefined) return answer.values;
      return { primaryPhone, cellphone: primaryPhone };
    }

    case "insurance":
    case "contact_info":
      return answer.values;

    case "autocomplete":
      return question.autocomplete
        ? { [question.autocomplete.name]: (answer.values as any)?.autocomplete }
        : answer.value;

    case "multi_column_nested":
    case "multi_column_nested_expansion": {
      const mainRows = answer.value || [];
      return mainRows
        .filter((row: any) => row.is_valid_row)
        .map((row: any) => ({
          ...row,
          nested_rows: (row.nested_rows || []).filter(
            (nr: any) => nr.is_valid_row
          ),
        }));
    }

    default:
      return answer.value;
  }
}

export function isHeightQuestion(qd: QuestionData): boolean {
  return qd.type === "select" && /height/i.test(qd.name);
}

export function isWeightQuestion(qd: QuestionData): boolean {
  return qd.type === "number" && /weight/i.test(qd.name);
}

const CM_PER_INCH = 2.54;
const LBS_PER_KG = 2.20462;
const HEIGHT_PATTERN = /^(\d+)\s*ft\s*(?:(\d+)\s*in)?$/i;

function formatHeightDual(label: string): string {
  const match = label.match(HEIGHT_PATTERN);
  if (!match) return label;
  const totalInches = parseInt(match[1], 10) * 12 + parseInt(match[2] || "0", 10);
  const cm = Math.round(totalInches * CM_PER_INCH);
  return `${label} (${cm} cm)`;
}

function formatWeightDual(lbs: number): string {
  const kg = Math.round(lbs / LBS_PER_KG);
  return `${lbs} lbs (${kg} kg)`;
}

export function formatAnswerForDisplay(answer: Answer): string | null {
  const qd = answer.question.data;
  const choices = qd.choices || qd.options || [];

  function lookupLabel(value: string): string {
    const match = choices.find((c) => String(c.value) === value);
    return match?.title || match?.label || match?.name || value;
  }

  if (isHeightQuestion(qd)) {
    const label = lookupLabel(String(answer.value ?? ""));
    return formatHeightDual(label);
  }

  if (isWeightQuestion(qd)) {
    const lbs = Number(answer.value);
    if (!isNaN(lbs) && lbs > 0) return formatWeightDual(lbs);
    return String(answer.value ?? "");
  }

  switch (qd.type) {
    case "display_text":
    case "extended_display_text":
    case "video":
      return null;

    case "choice":
    case "select": {
      const choiceKey = answer.values?.[0] as string | undefined;
      if (choiceKey) {
        const byKey = choices.find(
          (c) => (c.name ?? String(c.value)) === choiceKey
        );
        if (byKey) return byKey.title || byKey.label || byKey.name || choiceKey;
      }
      return lookupLabel(String(answer.value ?? ""));
    }

    case "choice_multi": {
      const vals = (answer.values || []).map((v: any) => lookupLabel(String(v.value)));
      return vals.join(", ");
    }

    case "multi_checkbox": {
      const checkedMap = answer.values?.[0] ?? answer.value ?? {};
      const selected = Object.keys(checkedMap).filter(
        (k) => checkedMap[k]
      );
      const boxTitleByName = new Map(
        (qd.checkboxes || []).map((cb) => [cb.name, cb.title ?? cb.name])
      );
      return selected
        .map((k) => boxTitleByName.get(k) ?? lookupLabel(k))
        .join(", ");
    }

    case "choice_multi_dropdown": {
      const lines = (answer.values || []).map((v: any) => {
        const choiceLabel = lookupLabel(String(v.value));
        if (v.selectVal) {
          const ddLabel = v.selectVal.title || v.selectVal.value;
          return `${choiceLabel}: ${ddLabel}`;
        }
        return choiceLabel;
      });
      return lines.join(", ");
    }

    case "columns_select_number": {
      const rows = (answer.value || []).filter(
        (v: any) => v.selectVal?.value !== undefined
      );
      return rows
        .map((v: any) => {
          const selLabel = v.selectVal.title || v.selectVal.value;
          return `${selLabel}: ${v.inputText ?? ""}`;
        })
        .join("; ");
    }

    case "contact_info": {
      const vals = answer.values || [];
      const parts: string[] = [];
      const first = vals.find((v: any) => /first/i.test(v.name || ""))?.value;
      const last = vals.find((v: any) => /last/i.test(v.name || ""))?.value;
      if (first || last) parts.push([first, last].filter(Boolean).join(" "));
      const email = vals.find((v: any) => /email/i.test(v.name || ""))?.value;
      if (email) parts.push(email);
      const phone = vals.find((v: any) => /phone/i.test(v.name || ""))?.value;
      if (phone) parts.push(phone);
      return parts.join(", ") || String(answer.value ?? "");
    }

    case "phone_number": {
      const phoneVals = answer.values || [];
      return phoneVals[0]?.value ?? String(answer.value ?? "");
    }

    case "multi_column_nested":
    case "multi_column_nested_expansion": {
      const rows = (answer.value || []).filter((r: any) => r.is_valid_row);
      if (rows.length === 0) return "";
      const tierOne = qd.columns_tier_one || [];
      return rows
        .map((row: any) => {
          const parts = tierOne
            .filter((c) => c.type !== "remove_button")
            .map((c) => {
              const v = row.values?.[c.name];
              if (!v) return null;
              const opt = c.options?.find((o) => String(o.value) === String(v));
              return opt ? opt.title || opt.label : v;
            })
            .filter(Boolean);
          return parts.join(" \u2014 ") || "Entry";
        })
        .join("; ");
    }

    case "date_input":
    case "month_date_input":
    case "future_date":
    case "text_input":
    case "paragraph_input":
    case "number":
    case "email_input":
      return String(answer.value ?? "");

    default:
      return String(answer.value ?? "");
  }
}

function isObject(val: any): val is Record<string, any> {
  return val !== null && typeof val === "object" && !Array.isArray(val);
}

const NON_INPUT_TYPES = new Set(["display_text", "extended_display_text", "video"]);

export function getPageErrors(
  page: Page,
  questions: Question[],
  answers: AnswerMap,
): Set<string> {
  const errors = new Set<string>();
  for (const pq of page.questions) {
    const question = questions[pq.index];
    if (!question) continue;
    const qd = question.data;
    if (NON_INPUT_TYPES.has(qd.type)) continue;
    if (!evaluateConditions(question, answers)) continue;

    const answer = answers[qd.name];
    if (qd.required && (!answer || !answer.isValid)) {
      errors.add(qd.name);
    } else if (answer && !answer.isValid) {
      errors.add(qd.name);
    }
  }
  return errors;
}

export function buildResponsePayload(
  questionnaire: Questionnaire,
  answers: AnswerMap,
  locale: string
): { survey: any } {
  const answerDict: Record<
    string,
    { answer: any; question: QuestionData }
  > = {};

  for (const key of Object.keys(answers)) {
    const answer = answers[key];
    if (!answer.isValid) continue;
    if (!evaluateConditions(answer.question, answers)) continue;

    const formatted = formatAnswer(answer);
    if (formatted === undefined) continue;

    const ansKey = getAnswerKey(answer);

    if (answerDict[ansKey] != null) {
      const previous = answerDict[ansKey].answer;
      if (Array.isArray(previous) && Array.isArray(formatted)) {
        previous.push(...formatted);
      } else if (isObject(previous) && isObject(formatted)) {
        Object.assign(previous, formatted);
      } else {
        answerDict[ansKey].answer = answer.question.data.is_part_of
          ? { [answer.question.data.name]: formatted }
          : formatted;
      }
    } else {
      answerDict[ansKey] = {
        answer: answer.question.data.is_part_of
          ? { [answer.question.data.name]: formatted }
          : formatted,
        question: answer.question.data,
      };
    }
  }

  const now = new Date();
  const uid =
    crypto.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    survey: {
      _id: `response_${questionnaire.id}_${uid}`,
      date: now.toDateString(),
      time: now.toTimeString(),
      survey_name: questionnaire.name,
      data: questionnaire.data,
      status: "Complete",
      type: "response",
      locale,
      modules: {
        ciq_risk_module: {
          title: questionnaire.title,
          items: answerDict,
        },
      },
    },
  };
}
